import { useState, useEffect } from 'react';
import api from '../api';
import { DollarSign, TrendingUp, BarChart2, Ticket, Bus, RefreshCw, TrendingDown, Wallet, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid
} from 'recharts';

const fmt = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-xs">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || p.fill }}>
          {p.name}: {p.name === 'Receita' ? `R$ ${fmt(p.value)}` : p.value}
        </p>
      ))}
    </div>
  );
}

export default function Relatorios() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get('/relatorios/financeiro')
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const kpiCards = data ? [
    {
      label: 'Receita Total',
      value: `R$ ${fmt(data.kpis.receita_total)}`,
      icon: DollarSign, accent: '#10B981', bg: '#DCFCE7', iconColor: '#10B981',
      sub: `${data.kpis.total_confirmadas} reservas confirmadas`
    },
    {
      label: 'Ticket Médio',
      value: `R$ ${fmt(data.kpis.ticket_medio)}`,
      icon: TrendingUp, accent: '#0147a0', bg: '#EEF2FF', iconColor: '#0147a0',
      sub: 'por reserva com valor'
    },
    {
      label: 'Taxa de Ocupação Média',
      value: fmtPct(data.kpis.taxa_ocupacao_media),
      icon: BarChart2, accent: '#F59E0B', bg: '#FFFBEB', iconColor: '#d97706',
      sub: 'em todas as viagens'
    },
    {
      label: 'Reservas Canceladas',
      value: data.kpis.total_canceladas,
      icon: Ticket, accent: '#EF4444', bg: '#FEE2E2', iconColor: '#EF4444',
      sub: 'total histórico'
    },
  ] : [];

  const caixaCards = data ? [
    {
      label: 'Total de Saídas',
      value: `R$ ${fmt(data.kpis.total_saidas)}`,
      icon: TrendingDown, accent: '#EF4444', bg: '#FEE2E2', iconColor: '#EF4444',
      sub: 'despesas + contas pagas'
    },
    {
      label: 'Saldo',
      value: `R$ ${fmt(data.kpis.saldo)}`,
      icon: Wallet, accent: data.kpis.saldo >= 0 ? '#10B981' : '#EF4444',
      bg: data.kpis.saldo >= 0 ? '#DCFCE7' : '#FEE2E2', iconColor: data.kpis.saldo >= 0 ? '#10B981' : '#EF4444',
      sub: 'receita − saídas'
    },
    {
      label: 'Contas a Pagar Pendentes',
      value: `R$ ${fmt(data.kpis.contas_pagar_pendente_total)}`,
      icon: AlertTriangle, accent: '#F59E0B', bg: '#FFFBEB', iconColor: '#d97706',
      sub: `${data.kpis.contas_vencidas_count} vencida(s), ${data.kpis.contas_a_vencer_count} a vencer`
    },
  ] : [];

  return (
    <div className="space-y-6 page-enter" data-testid="relatorios-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Relatórios Financeiros
          </h1>
          <p className="text-slate-500 text-sm mt-0.5">Análise completa da operação — visão geral</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="btn-atualizar-relatorios"
          className="flex items-center gap-2 text-slate-600 border border-slate-300 hover:bg-slate-50 font-medium px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-slate-400">
          <RefreshCw className="w-6 h-6 animate-spin mr-3" />
          <span>Carregando relatórios...</span>
        </div>
      ) : !data ? (
        <div className="text-center py-20 text-slate-400">Erro ao carregar dados. Tente novamente.</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-cards-relatorios">
            {kpiCards.map((card) => (
              <div
                key={card.label}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 relative overflow-hidden"
                style={{ borderTop: `3px solid ${card.accent}` }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{card.label}</p>
                    <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {card.value}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">{card.sub}</p>
                  </div>
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: card.bg }}
                  >
                    <card.icon className="w-5 h-5" style={{ color: card.iconColor }} />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Alerta de contas vencidas */}
          {data.kpis.contas_vencidas_count > 0 && (
            <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl px-5 py-3.5">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <p className="text-sm text-red-700">
                <span className="font-bold">{data.kpis.contas_vencidas_count}</span> {data.kpis.contas_vencidas_count === 1 ? 'conta a pagar vencida' : 'contas a pagar vencidas'} somando <span className="font-bold">R$ {fmt(data.kpis.contas_vencidas_total)}</span>
              </p>
            </div>
          )}

          {/* Fluxo de Caixa */}
          <div>
            <h2 className="font-bold text-slate-800 text-base mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Fluxo de Caixa
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="kpi-cards-caixa">
              {caixaCards.map((card) => (
                <div
                  key={card.label}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 relative overflow-hidden"
                  style={{ borderTop: `3px solid ${card.accent}` }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">{card.label}</p>
                      <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {card.value}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">{card.sub}</p>
                    </div>
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: card.bg }}
                    >
                      <card.icon className="w-5 h-5" style={{ color: card.iconColor }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Monthly Revenue Chart */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="grafico-receita-mensal">
              <div className="mb-4">
                <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Receita por Mês
                </h2>
                <p className="text-xs text-slate-400">Últimos 6 meses</p>
              </div>
              {data.receita_mensal.every(m => m.receita === 0) ? (
                <div className="h-48 flex items-center justify-center text-slate-300 text-sm">
                  Nenhuma receita registrada no período
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.receita_mensal} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false}
                      tickFormatter={(v) => v >= 1000 ? `R$${(v / 1000).toFixed(0)}k` : `R$${v}`} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="receita" name="Receita" fill="#0147a0" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Monthly Reservations Chart */}
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="grafico-reservas-mensais">
              <div className="mb-4">
                <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Reservas por Mês
                </h2>
                <p className="text-xs text-slate-400">Últimos 6 meses</p>
              </div>
              {data.receita_mensal.every(m => m.reservas === 0) ? (
                <div className="h-48 flex items-center justify-center text-slate-300 text-sm">
                  Nenhuma reserva registrada no período
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={data.receita_mensal} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="reservas" name="Reservas" fill="#d72d21" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Top Trips Table */}
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-testid="tabela-viagens-receita">
            <div className="p-5 border-b border-slate-100">
              <h2 className="font-bold text-slate-800 text-base" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Top Viagens por Receita
              </h2>
              <p className="text-xs text-slate-400">Até 10 viagens com maior receita gerada</p>
            </div>
            {data.viagens_resumo.length === 0 ? (
              <div className="p-10 text-center text-slate-400 text-sm">
                <Bus className="w-8 h-8 mx-auto mb-2 opacity-20" />
                Nenhuma viagem com receita registrada
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Viagem</th>
                      <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Data</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Passageiros</th>
                      <th className="text-center px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider hidden lg:table-cell">Ocupação</th>
                      <th className="text-right px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Receita</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {data.viagens_resumo.map((v, i) => (
                      <tr key={v.viagem_id || `row-${i}`} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="font-semibold text-slate-800">{v.titulo}</p>
                          <p className="text-xs text-slate-400">{v.rota}</p>
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          <p className="text-xs text-slate-600">
                            {v.data ? new Date(v.data + 'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                          </p>
                        </td>
                        <td className="px-5 py-3.5 text-center">
                          <span className="text-sm font-semibold text-slate-800">{v.passageiros}</span>
                          <span className="text-xs text-slate-400">/{v.capacidade}</span>
                        </td>
                        <td className="px-5 py-3.5 hidden lg:table-cell">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${Math.min(v.taxa_ocupacao, 100)}%`,
                                  background: v.taxa_ocupacao >= 80 ? '#10B981' : v.taxa_ocupacao >= 50 ? '#F59E0B' : '#EF4444'
                                }}
                              />
                            </div>
                            <span className="text-xs font-semibold text-slate-600 w-10 text-right">{fmtPct(v.taxa_ocupacao)}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <span className="font-bold text-emerald-600">R$ {fmt(v.receita)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Top Destinos */}
          {data.top_destinos?.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-testid="top-destinos-card">
              <h2 className="font-bold text-slate-800 text-base mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Destinos mais Frequentes
              </h2>
              <div className="space-y-3">
                {data.top_destinos.map((d, i) => {
                  const max = data.top_destinos[0].viagens;
                  const pct = (d.viagens / max) * 100;
                  return (
                    <div key={d.destino || `destino-${i}`} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-slate-400 w-5">{i + 1}.</span>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-slate-700">{d.destino}</span>
                          <span className="text-xs text-slate-500">{d.viagens} viagem(ns)</span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: '#0147a0' }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
